import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import ProductAction from 'components/Blocks/Product/Action/ProductAction';
import ProductAvailableStoresCount, {
    getProductAvailabilityEShopTwClass,
    productAvailabilityTwClass,
} from 'components/Blocks/Product/Availability/ProductAvailableStoresCount';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import ProductFlags from 'components/Blocks/Product/Flags/ProductFlags';
import { ProductCatnum } from 'components/Blocks/Product/List/ListedItem/ProductCatnum';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import ProductPrice from 'components/Blocks/Product/Price/ProductPrice';
import SimpleColorVariants from 'components/Blocks/Product/SimpleColorVariants/SimpleColorVariants';
import { ProductWishlistButton } from 'components/Blocks/Product/Wishlist/ProductWishlistButtons';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isB2cDomain, isWebDomain } from 'helpers/isDomainLocale';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { CSSProperties } from 'react';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum } from 'types/availability';
import { GtmListNameType } from 'types/gtm';
import { SliderProductItemType } from 'types/product';
import { onClickProductDetailGtmEventHandler } from 'utils/Gtm/EventHandlers';

type SliderProductItemProps = {
    idSuffix?: string;
    product: SliderProductItemType;
    gtmListName: GtmListNameType;
    listIndex: number;
    selected: boolean;
    isContentVisible: boolean;
    shouldRedirectOnClick?: boolean;
    onProductItemClick?: (product: SliderProductItemType) => void;
    isConfigurator?: boolean;
    isProductInWishlist: boolean;
    toggleProductInWishlist: () => void;
    maxVisibleItems?: number;
    onAddToCart?: (productUuid: string) => void;
};

export const SliderProductItem: FC<SliderProductItemProps> = ({
    idSuffix,
    gtmListName,
    listIndex,
    product,
    selected,
    isContentVisible,
    onProductItemClick,
    shouldRedirectOnClick = true,
    isConfigurator,
    isProductInWishlist,
    toggleProductInWishlist,
    maxVisibleItems,
    onAddToCart,
}) => {
    const testIdentifier = 'blocks-product-sliderproductitem-';
    const domainConfig = useDomainConfig();
    const { t } = useTranslation();

    const { isUserLoggedIn } = useCurrentUserData();
    const isActionPrice = product.price.priceWithoutVat < product.prices.basic.priceWithoutVat;
    const isElectronicGiftVoucher = product.isElectronicGiftVoucher;

    const getSliderItemWidthDivisor = (defaultValue: number) =>
        maxVisibleItems ? Math.min(maxVisibleItems, defaultValue) : defaultValue;

    return (
        <div
            data-testid={testIdentifier + product.catalogNumber}
            className={twJoin(
                'relative flex min-h-full w-[calc(100%-120px)] flex-shrink-0 flex-col justify-between px-2 py-5 text-primary',
                'md:w-[calc(100%/var(--len-md))]',
                'lg:w-[calc(100%/var(--len-lg))]',
                'vl:w-[calc(100%/var(--len-vl))]',
                'xl:w-[calc(100%/var(--len-xl))]',
                'lg:after:absolute lg:after:bottom-0 lg:after:left-0 lg:after:right-0 lg:after:top-0 lg:after:-z-above lg:after:hidden lg:after:rounded-md lg:after:bg-white lg:after:content-[""]',
                'hover:after:lg:block',
                !selected && 'pointer-events-none opacity-[50] md:pointer-events-auto md:opacity-100',
            )}
            style={
                {
                    '--len-md': getSliderItemWidthDivisor(2),
                    '--len-lg': getSliderItemWidthDivisor(3),
                    '--len-vl': getSliderItemWidthDivisor(4),
                    '--len-xl': getSliderItemWidthDivisor(5),
                } as CSSProperties
            }
            onClick={() => onProductItemClick?.(product)}
        >
            {isContentVisible && (
                <div className="flex flex-1 flex-col justify-between">
                    <div className="absolute right-2 top-2 z-above">
                        <ProductWishlistButton
                            className="mb-0"
                            isProductInWishlist={isProductInWishlist}
                            toggleProductInWishlist={toggleProductInWishlist}
                        />
                    </div>
                    <ExtendedNextLink
                        className="relative flex flex-1 flex-col justify-between no-underline hover:no-underline not-last:pb-0"
                        href={product.slug}
                        shouldRedirectOnClick={shouldRedirectOnClick}
                        type="product"
                        onClick={() =>
                            onClickProductDetailGtmEventHandler(product, gtmListName, listIndex, domainConfig.url)
                        }
                    >
                        <>
                            <div className="flex flex-1 flex-col">
                                <div
                                    className="relative mb-1 flex h-[170px] w-full items-center justify-center bg-transparent text-[0] md:h-[180px]"
                                    data-testid={testIdentifier + 'image'}
                                >
                                    <div className="absolute bottom-0 left-0 right-12 z-above flex flex-row flex-wrap items-start gap-1 text-body">
                                        <ProductFlags flags={product.flags} isProductDetail={false} product={product} />
                                    </div>
                                    <div className="relative flex h-[170px] w-full max-w-[170px] items-center justify-center md:h-[180px] md:max-w-[180px]">
                                        <Image
                                            alt={product.fullName}
                                            className="max-h-full max-w-full mix-blend-multiply"
                                            height={180}
                                            src={product.mainImage?.url ?? ''}
                                            width={180}
                                        />
                                    </div>
                                </div>
                                <SimpleColorVariants colorVariants={product.colorVariants} />
                                {product.colorVariants.length === 0 && <div className="max-h-7 flex-1" />}
                                <h3
                                    data-testid={testIdentifier + 'name'}
                                    className={twJoin(
                                        'mb-[15px] line-clamp-4 block overflow-hidden text-body font-normal tracking-wider text-black [word-wrap:break-word]',
                                        product.fullName.length > 50 ? 'leading-5' : 'leading-6',
                                        product.colorVariants.length === 0 && 'pt-2',
                                    )}
                                >
                                    {product.fullName}
                                </h3>
                                <ProductCatnum catalogNumber={product.catalogNumber} className="mb-[15px] mt-0" />
                            </div>

                            {!isWebDomain(domainConfig) && (
                                <div>
                                    {isB2cDomain(domainConfig) && (
                                        <div className="mt-auto text-small">
                                            {product.prices.tescoma && isUserLoggedIn === false && (
                                                <ProductPrice {...product.prices.tescoma} isTescomaPrice />
                                            )}
                                            {isActionPrice && <ProductPrice {...product.prices.basic} isOldPrice />}
                                        </div>
                                    )}
                                    <div className="slider-product-item__bottom">
                                        {isB2bDomain(domainConfig) ? (
                                            <>
                                                <B2bProductPrice
                                                    b2bPaymentDiscount={product.b2bPaymentDiscount}
                                                    basePrice={product.prices.basic}
                                                    isUnitPrice={(product.b2bMinimumProductOrder ?? 0) > 1}
                                                    sellingPrice={product.price}
                                                    variant="list"
                                                />
                                                <ProductStock
                                                    className="mt-[15px]"
                                                    minimumSellingQuantity={product.b2bMinimumProductOrder ?? 1}
                                                    quantity={product.stockQuantity}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <ProductPrice {...product.price} isActionPrice={isActionPrice} />
                                                <div className={twJoin(productAvailabilityTwClass, 'mt-[15px]')}>
                                                    <span
                                                        className={getProductAvailabilityEShopTwClass(
                                                            product.availabilityStatus ===
                                                                AvailabilityStatusEnum.inStock,
                                                        )}
                                                    >
                                                        {product.availability.status === AvailabilityStatusEnum.inStock
                                                            ? t('Available in eshop')
                                                            : t('Unavailable in eshop')}
                                                    </span>
                                                    {!isElectronicGiftVoucher && (
                                                        <ProductAvailableStoresCount
                                                            availableStoresCount={product.availableStoresCount}
                                                            isMainVariant={product.isMainVariant}
                                                            deliveryDaysFromCentralStore={
                                                                product.deliveryDaysFromCentralStore
                                                            }
                                                            isInCentralStore={
                                                                product.availabilityStatus ===
                                                                AvailabilityStatusEnum.inStock
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    </ExtendedNextLink>
                    <ProductAction
                        gtmListName={gtmListName}
                        idSuffix={idSuffix}
                        isConfigurator={isConfigurator}
                        listIndex={listIndex}
                        product={product}
                        onAddToCart={onAddToCart}
                    />
                </div>
            )}
        </div>
    );
};
